import React, { FC, useCallback, useEffect, useState } from "react";

import Page from "../common/navigation/Page";
import Button from "../common/F7/Button";
import ButtonSmall from "../common/F7/ButtonSmall";
import ListBlock from "../common/F7/ListBlock";
import ListBlockItem from "../common/F7/ListBlockItem";
import ContentTitle from "../common/F7/ContentTitle";

import FormFields from "../common/form/FieldsList";
import FormButtons from "../common/form/Buttons";

import F7 from "../../utils/F7";

import SaveButton from "../common/F7/SaveButton";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useCurrentProject } from "../../hooks/database/useCurrentProject";
import { useUpdateFormTemplate, useUpdateInventory } from "../../hooks/database/usePouchUpdate";
import { ProjectField } from "../../hooks/database/types/projects";
import { useFormTemplates } from "../../hooks/database/usePouchAllDocs";
import { useForm } from "../../hooks/useForm";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { useCurrentInventoryFormId } from "../../hooks/url";
import { useCreateFormTemplate } from "../../hooks/database/usePouchCreate";
import { useDeleteFormTemplate } from "../../hooks/database/usePouchDelete";
import EditFormField from "./components/EditFormField";
import BackButton from "../common/navigation/BackButton";
import "../../index.scss";

const generateFormIdFromName = (name: string) => {
  if (name) {
    return name
      .toLowerCase()
      .replace(/å/g, "a")
      .replace(/ä/g, "a")
      .replace(/ö/g, "o")
      .replace(/[^a-z1-9]/g, "_");
  } else {
    return name;
  }
};

const InventoryFormEditPage: FC = () => {
  const { inventoryId, formId } = useCurrentInventoryFormId();

  const { data: formTemplates } = useFormTemplates();
  const { data: project } = useCurrentProject();
  const { data: inventory } = useCurrentInventory();
  const { execute: updateInventory } = useUpdateInventory();

  const [fields, setFields] = useState<Record<string, ProjectField>>({});
  const [changeFields, setChangeFields] = useState(false);
  const {
    //dirty,
    values,
    fields: formFields,
    handleChange,
    handleBlur,
  } = useForm({
    name: {
      label: "Template Name",
      type: "text",
      value: inventory && formId ? inventory.forms[formId].name : undefined,
    },
  });

  useEffect(() => {
    if (formId && inventory) {
      setFields(inventory.forms[formId].fields);
    }
  }, [formId, inventory]);

  const navigate = useNavigate();

  const handleSave = useCallback(async () => {
    if (!project || !inventory || !inventoryId) {
      return;
    }

    const formName = values.name as string;
    if (!formName || formName.length < 1) {
      F7.alert("", "Template Name is required");
      return;
    } else if (formName.length > 0 && formName.charCodeAt(0) === 32) {
      F7.alert("", "Template Name can not start with a blankspace");
      return;
    }
    const formKey = formId || generateFormIdFromName(formName);

    if (!formKey || formKey.length < 1) {
      F7.alert("", "Template Name is required");
      return;
    } else if (formKey.length > 0 && formKey.charCodeAt(0) === 32) {
      F7.alert("", "Template Name can not start with a blankspace");
      return;
    }

    const inventoryName = inventory.name;

    if (!inventoryName || inventoryName.length < 1) {
      F7.alert("", "Inventory Name is required");
      return;
    }
    if (inventoryName.length > 0 && inventoryName.charCodeAt(0) === 32) {
      F7.alert("", "Inventory Name can not start with a blankspace");
      return;
    }

    await updateInventory(inventoryId, {
      ...inventory,
      forms: {
        ...inventory.forms,
        [formKey]: {
          key: formKey,
          name: formName,
          fields,
        },
      },
    });
  }, [project, inventory, inventoryId, values.name, formId, updateInventory, fields]);

  const handleCancel = useCallback(() => {
    navigate("../../edit");
  }, [navigate]);

  /*const handleFieldClick = useCallback(
    (field: ProjectField) => {
      if (!dirty && formId) {
        navigate(field.key);
      }
    },
    [dirty, formId, navigate],
  );*/

  const { execute: updateFormTemplate } = useUpdateFormTemplate();
  const handleSaveExistingFormTemplateClick = useCallback(() => {
    if (!formTemplates) {
      return;
    }
    F7.actions([
      formTemplates.length > 0
        ? [
            { text: "Overwrite template?", label: true },
            ...formTemplates.map((template) => ({
              text: template.name,
              onClick: async () =>
                await updateFormTemplate({
                  ...template,
                  fields,
                }),
              label: false,
            })),
          ]
        : [
            {
              text: "No templates found",
              label: true,
            },
          ],
      [
        {
          text: "Cancel",
        },
      ],
    ]);
  }, [fields, formTemplates, updateFormTemplate]);
  const { execute: createFormTemplate } = useCreateFormTemplate();
  const handleSaveAsFormTemplateClick = useCallback(() => {
    F7.prompt("Enter a Template name", "Template Name", async (value: string) => {
      await createFormTemplate({
        name: value,
        fields,
      });
    });
  }, [createFormTemplate, fields]);
  const handleDeleteFieldClick = useCallback((fieldKey: string, e: any) => {
    e.stopPropagation();
    setFields((fields) => Object.fromEntries(Object.entries(fields || {}).filter(([key, _]) => key !== fieldKey)));
  }, []);
  const loadFieldsFromTemplate = useCallback(
    (templateFields: object) => {
      setFields({ ...fields, ...templateFields });
    },
    [fields],
  );
  const handleSelectTemplateClick = useCallback(() => {
    if (!formTemplates) {
      return;
    }
    const templateButtons = formTemplates.map((template) => {
      return {
        text: template.name,
        onClick: () => loadFieldsFromTemplate(template.fields),
        label: false,
      };
    });

    if (templateButtons.length < 1) {
      templateButtons.push({
        text: "No Templates Found",
        label: true,
        onClick: () => {},
      });
    }

    F7.actions([
      templateButtons,
      [
        {
          text: "Cancel",
        },
      ],
    ]);
  }, [formTemplates, loadFieldsFromTemplate]);
  const { execute: deleteFormTemplate } = useDeleteFormTemplate();
  const handleDeleteFormTemplateClick = useCallback(() => {
    if (!formTemplates) {
      return;
    }
    F7.actions([
      formTemplates.length > 0
        ? [
            { text: "Delete Template?", label: true },
            ...formTemplates.map((template) => ({
              text: template.name,
              onClick: async () => {
                await deleteFormTemplate(template);
              },
              label: false,
            })),
          ]
        : [{ text: "No templates found", label: true }],
      [
        {
          text: "Cancel",
        },
      ],
    ]);
  }, [deleteFormTemplate, formTemplates]);
  const handleEditToggle = useCallback(() => {
    setChangeFields((current) => !current);
  }, []);

  const handleSaveField = useCallback((field: ProjectField) => {
    setFields((f) => ({ ...f, [field.key]: field }));
  }, []);

  return (
    <Page title="Template Editor" navbarLeft={<BackButton onClick={handleCancel}>Close editor</BackButton>} noToolbar>
      <Routes>
        <Route path="add" element={<EditFormField fields={fields} onFieldSave={handleSaveField} />} />
        <Route path=":field" element={<EditFormField fields={fields} onFieldSave={handleSaveField} />} />
        <Route
          index
          element={
            <>
              <div>
                <FormFields fields={formFields} onChange={handleChange} onBlur={handleBlur} />
              </div>

              <div>
                <ContentTitle>Template Fields</ContentTitle>
                <div className="buttonContainer">
                  {Object.keys(fields).length ? (
                    <ButtonSmall onClick={handleEditToggle}>{changeFields ? "Done" : "Change"}</ButtonSmall>
                  ) : null}
                </div>
                <ListBlock>
                  {Object.values(fields).map((field) => (
                    <ListBlockItem
                      media={
                        changeFields ? (
                          <i className="fa fa-minus-circle removeIcon" onClick={(e) => handleDeleteFieldClick(field.key, e)} />
                        ) : null
                      }
                      key={field.sortKey || field.key}
                      sortKey={field.key}
                      title={field.label}
                      to={`../${field.key}`}
                      //onClick={() => handleFieldClick(field)}
                    />
                  ))}
                </ListBlock>

                <Button to="add">Add Field</Button>
                <Button onClick={handleSelectTemplateClick}>Add Fields From Template Store</Button>
                <hr />
                <FormButtons saveText="Save Template To Inventory" onSave={handleSave} />
                <hr />
                {Object.keys(fields).length ? <SaveButton onSave={handleSaveAsFormTemplateClick}>Save To Template Store</SaveButton> : null}
                {Object.keys(fields).length ? (
                  <Button onClick={handleSaveExistingFormTemplateClick}>Update Existing In Template Store</Button>
                ) : null}
                <Button onClick={handleDeleteFormTemplateClick}>Delete Existing In Template Store</Button>
                <hr />
              </div>
            </>
          }
        />
      </Routes>
    </Page>
  );
};

export default InventoryFormEditPage;
